import React, { useState, useCallback } from 'react';
import {
  Drawer, Box, Typography, IconButton, Tabs, Tab,
  Button, Chip, CircularProgress,
} from '@mui/material';
import { Close, LocalOffer } from '@mui/icons-material';
import { useDispatch, useSelector } from 'react-redux';
import { useGetPosDiscountsQuery } from '../../services/discountApi';
import { useUpdateOrderMutation } from '../../services/orderApi';
import { setCurrentOrder } from '../../store/orderSlice';
import { setPendingApproval } from '../../store/discountSlice';
import { useToast } from '../common/ToastProvider';
import BillDiscountTab from './discount/BillDiscountTab';
import ItemDiscountTab from './discount/ItemDiscountTab';

const DiscountDrawer = ({ open, onClose }) => {
  const dispatch  = useDispatch();
  const showToast = useToast();

  const [tab, setTab]           = useState(0);
  const [selection, setSelection] = useState(null);
  const [removingBill, setRemovingBill]       = useState(false);
  const [removingItemId, setRemovingItemId]   = useState(null);

  const {
    currentOrderId, cartItems: orderCartItems,
    subtotal, bill_discount_id, bill_discount_name, bill_discount_amount,
  } = useSelector((s) => s.order);

  // Build display cart from synced backend items for ItemDiscountTab
  const displayCart = orderCartItems.map((oi) => ({
    id:           oi.product_id,
    name:         oi.product_name,
    qty:          oi.quantity,
    retail_price: oi.unit_price,
  }));

  const { data: billDiscountsData, isLoading } = useGetPosDiscountsQuery(
    { applicable_level: 'bill' },
    { skip: !open, refetchOnMountOrArgChange: 30 },
  );
  const billDiscounts = billDiscountsData?.data?.discounts || [];

  const [updateOrder, { isLoading: isApplying }] = useUpdateOrderMutation();

  const handleSelectionChange = useCallback((sel) => setSelection(sel), []);

  // ── Remove helpers ─────────────────────────────────────────────

  const handleRemoveBill = async () => {
    if (!currentOrderId) return;
    setRemovingBill(true);
    try {
      const res = await updateOrder({ id: currentOrderId, bill_discount: null }).unwrap();
      dispatch(setCurrentOrder(res.data.order));
      showToast('Bill discount removed', 'info');
    } catch (err) {
      showToast(err?.data?.error || 'Failed to remove discount', 'error');
    } finally { setRemovingBill(false); }
  };

  const handleRemoveItem = async (itemId) => {
    if (!currentOrderId) return;
    setRemovingItemId(itemId);
    try {
      const res = await updateOrder({
        id: currentOrderId,
        line_item_discounts: [{ order_item_id: itemId, discount_id: null }],
      }).unwrap();
      dispatch(setCurrentOrder(res.data.order));
      showToast('Item discount removed', 'info');
    } catch (err) {
      showToast(err?.data?.error || 'Failed to remove discount', 'error');
    } finally { setRemovingItemId(null); }
  };

  // ── Apply ──────────────────────────────────────────────────────

  const handleApply = async () => {
    if (!currentOrderId || !selection?.discount) return;
    const d      = selection.discount;
    const parsed = selection.customValue !== '' ? parseFloat(selection.customValue) : NaN;
    const modVal = Number.isFinite(parsed) ? parsed : null;
    const reason = selection.reason || null;

    const payload = tab === 0
      ? { bill_discount: { discount_id: d.id, modified_value: modVal, reason } }
      : { line_item_discounts: [{ order_item_id: selection.item?.oci?.order_item_id, discount_id: d.id, modified_value: modVal, reason }] };

    try {
      const res = await updateOrder({ id: currentOrderId, ...payload }).unwrap();

      // pending_approval may live at res.data or res root depending on backend serializer
      const isPending  = !!(res?.data?.pending_approval  ?? res?.pending_approval);
      const approvalId =    res?.data?.approval_request_id ?? res?.approval_request_id;
      const order      =    res?.data?.order;

      if (order) dispatch(setCurrentOrder(order));

      if (isPending) {
        // DiscountApprovalModal (mounted at Dashboard.js level) takes over
        // the instant this fires — cart and order are left untouched.
        dispatch(setPendingApproval({
          requestId:    approvalId,
          discountName: d.name,
          level:        tab === 0 ? 'bill' : 'line_item',
          orderId:      currentOrderId,
        }));
        showToast(`${d.name} sent for manager approval`, 'warning');
        handleClose();
      } else {
        showToast('Discount applied!', 'success');
        handleClose();
      }
    } catch (err) {
      showToast(err?.data?.error || 'Failed to apply discount', 'error');
    }
  };

  const handleClose = () => {
    setSelection(null);
    onClose();
  };

  // ── Derived state for footer ───────────────────────────────────

  const canApply = !!selection?.discount;

  // belowMin — disable Apply when order/item subtotal is below the discount's minimum
  const itemSubtotal = tab === 1 && selection?.item?.oci
    ? parseFloat(selection.item.oci.unit_price || 0) * parseFloat(selection.item.oci.quantity || 1)
    : 0;
  const effectiveSub = tab === 0 ? parseFloat(subtotal || 0) : itemSubtotal;
  const minBill      = parseFloat(selection?.discount?.minimum_bill_value || 0);
  const belowMin     = canApply && minBill > 0 && effectiveSub < minBill;

  const discountCount = (bill_discount_id ? 1 : 0) + orderCartItems.filter((i) => i.discount_id).length;

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={handleClose}
      PaperProps={{
        sx: { width: { xs: '100vw', sm: 420 }, maxWidth: '100vw', display: 'flex', flexDirection: 'column' },
      }}
    >
      {/* Header */}
      <Box sx={{
        px: 2.5, py: 2, borderBottom: '1px solid #F1F5F9',
        display: 'flex', alignItems: 'center', gap: 1.5, flexShrink: 0,
      }}>
        <LocalOffer sx={{ color: '#4F46E5', fontSize: 20 }} />
        <Typography fontWeight={800} fontSize="1rem" color="#1E293B" sx={{ flexGrow: 1 }}>
          Apply Discount
        </Typography>
        {discountCount > 0 && (
          <Chip
            size="small"
            label={`${discountCount} applied`}
            sx={{ height: 20, fontSize: '0.62rem', fontWeight: 700, bgcolor: '#F0FDF4', color: '#16A34A' }}
          />
        )}
        <IconButton size="small" onClick={handleClose} sx={{ color: '#64748B' }}>
          <Close fontSize="small" />
        </IconButton>
      </Box>

      {/* Tabs */}
      <Tabs
        value={tab}
        onChange={(_, v) => { setTab(v); setSelection(null); }}
        sx={{
          flexShrink: 0,
          borderBottom: '1px solid #F1F5F9',
          '& .MuiTab-root': { fontSize: '0.78rem', fontWeight: 700, textTransform: 'none', minHeight: 44 },
          '& .MuiTabs-indicator': { bgcolor: '#4F46E5' },
        }}
      >
        <Tab label="Bill Discount" />
        <Tab label="Item Discount" disabled={orderCartItems.length === 0} />
      </Tabs>

      {/* Body */}
      {isLoading ? (
        <Box sx={{ flexGrow: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <CircularProgress size={28} sx={{ color: '#4F46E5' }} />
        </Box>
      ) : tab === 0 ? (
        <BillDiscountTab
          discounts={billDiscounts}
          orderSubtotal={subtotal}
          billDiscountId={bill_discount_id}
          billDiscountName={bill_discount_name}
          billDiscountAmount={bill_discount_amount}
          onSelectionChange={handleSelectionChange}
          onRemove={handleRemoveBill}
          isRemoving={removingBill}
        />
      ) : (
        <ItemDiscountTab
          cart={displayCart}
          orderCartItems={orderCartItems}
          onSelectionChange={handleSelectionChange}
          onRemoveItem={handleRemoveItem}
          removingItemId={removingItemId}
        />
      )}

      {/* Footer */}
      <Box sx={{ flexShrink: 0, px: 2.5, py: 2, borderTop: '1px solid #F1F5F9', display: 'flex', gap: 1 }}>
        <Button
          fullWidth variant="outlined"
          onClick={handleClose}
          sx={{ fontWeight: 700, textTransform: 'none', borderRadius: '8px', borderColor: '#E2E8F0', color: '#64748B' }}
        >
          Cancel
        </Button>
        <Button
          fullWidth variant="contained" disableElevation
          onClick={handleApply}
          disabled={!canApply || isApplying || belowMin}
          sx={{
            fontWeight: 800, textTransform: 'none', borderRadius: '8px',
            bgcolor: '#4F46E5', color: '#FFF',
            '&:hover': { bgcolor: '#4338CA' },
            '&.Mui-disabled': { bgcolor: '#E2E8F0', color: '#94A3B8' },
          }}
        >
          {isApplying ? <CircularProgress size={18} sx={{ color: '#FFF' }} /> : 'Apply Discount'}
        </Button>
      </Box>
    </Drawer>
  );
};

export default DiscountDrawer;
